import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, Eye, Tag, ChevronRight, Search, ArrowRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Input } from "@/components/ui/input";
import API from "@/lib/api";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const CATEGORIES = ["All", "SMS Marketing", "E-Commerce", "SMS Delivery", "Getting Started", "Security", "Developer", "Finance"];

const BLOG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "url": "https://www.sendoras.com/blog",
  "name": "Sendora Blog — Bulk SMS Insights for Nigerian Businesses",
  "description": "Guides, tips and strategies for SMS marketing in Nigeria",
};

function BlogCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid="blog-card"
      className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
    >
      <div className="aspect-[16/9] overflow-hidden bg-slate-100">
        {post.image_url ? (
          <img
            src={post.image_url}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0c1e41] to-[#1e3a6e] flex items-center justify-center">
            <span className="text-white/40 text-4xl font-bold">{post.title[0]}</span>
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] font-bold bg-[#eef1f8] text-[#0c1e41] px-2.5 py-1 rounded-full uppercase tracking-wide">
            {post.category}
          </span>
        </div>
        <h2 className="text-base font-bold text-slate-900 leading-snug mb-2 group-hover:text-[#0c1e41] line-clamp-2">
          {post.title}
        </h2>
        <p className="text-sm text-slate-500 line-clamp-2 mb-4">{post.excerpt}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span className="flex items-center gap-1"><Clock size={11} /> {post.read_time} min read</span>
            <span className="flex items-center gap-1"><Eye size={11} /> {(post.views || 0).toLocaleString()}</span>
          </div>
          <ArrowRight size={14} className="text-[#0c1e41] opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </Link>
  );
}

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");

  useEffect(() => { loadBlogs(); }, [page, category]);

  const loadBlogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 12 });
      if (category !== "All") params.set("category", category);
      const { data } = await API.get(`/blogs?${params}`);
      setBlogs(data.blogs || []);
      setTotal(data.total || 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const filtered = search.trim()
    ? blogs.filter(b => b.title.toLowerCase().includes(search.toLowerCase()) || b.excerpt.toLowerCase().includes(search.toLowerCase()))
    : blogs;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Blog — Bulk SMS Marketing Guides for Nigerian Businesses"
        description="Read Sendora's expert guides on bulk SMS marketing, delivery rates, API integration, Sender IDs and more — tailored for Nigerian businesses."
        canonical="/blog"
        keywords="bulk SMS marketing Nigeria, SMS tips Nigeria, Sendora blog, Nigerian business SMS guide, SMS API tutorial"
        schema={BLOG_SCHEMA}
      />

      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
          {/* Nav breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-6">
            <Link to="/" className="hover:text-[#0c1e41]">Home</Link>
            <ChevronRight size={14} />
            <span className="text-slate-700 font-medium">Blog</span>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <img src="/sendora-fav.webp" alt="Sendora" className="h-8 w-auto" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#0c1e41] bg-[#eef1f8] px-3 py-1 rounded-full">
              Sendora Blog
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-3">
            SMS Marketing Insights
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl">
            Guides, tips and strategies for Nigerian businesses — from delivery rates and Sender IDs to API integration and campaign optimisation.
          </p>

          {/* Search */}
          <div className="mt-6 max-w-md relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="blog-search"
              placeholder="Search articles..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 border-slate-200 bg-slate-50 h-10"
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-category-filter">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => { setCategory(cat); setPage(1); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${category === cat ? "bg-[#0c1e41] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0c1e41] hover:text-[#0c1e41]"}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Blog grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="aspect-[16/9] bg-slate-200" />
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-slate-200 rounded w-1/3" />
                  <div className="h-4 bg-slate-200 rounded w-5/6" />
                  <div className="h-3 bg-slate-200 rounded w-4/6" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-400 text-lg">No articles found</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-400 mb-5">{total} article{total !== 1 ? "s" : ""}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(post => <BlogCard key={post._id} post={post} />)}
            </div>
          </>
        )}

        {/* Pagination */}
        {Math.ceil(total / 12) > 1 && !search && (
          <div className="flex justify-center items-center gap-3 mt-10">
            <button
              onClick={() => setPage(p => p - 1)} disabled={page === 1}
              className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:border-[#0c1e41] disabled:opacity-40 bg-white"
            >
              Previous
            </button>
            <span className="text-sm text-slate-500">Page {page} of {Math.ceil(total / 12)}</span>
            <button
              onClick={() => setPage(p => p + 1)} disabled={blogs.length < 12}
              className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:border-[#0c1e41] disabled:opacity-40 bg-white"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-[#0c1e41] text-white/60 text-center text-xs py-6 mt-12">
        <p>© {new Date().getFullYear()} Sendora · <Link to="/privacy" className="hover:text-white">Privacy</Link> · <Link to="/terms" className="hover:text-white">Terms</Link></p>
      </footer>
      <WhatsAppButton />
    </div>
  );
}
